import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  Button,
  HStack,
  Badge,
  Spinner,
  Center,
  Text,
  Select,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Stack,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import PiketService from "services/report/piketService.js";
import scheduleService from "services/report/scheduleService.js";
import Card from "components/Card/Card.js";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function PiketDashboard() {
  const [jadwal, setJadwal] = useState([]);
  const [regu, setRegu] = useState([]);
  const [tukar, setTukar] = useState([]);
  const [pegawai, setPegawai] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTgl, setFilterTgl] = useState(todayStr());
  const toast = useToast();
  const jadwalModal = useDisclosure();
  const reguModal = useDisclosure();
  const tukarModal = useDisclosure();

  const [formJadwal, setFormJadwal] = useState({ tanggal: todayStr(), regu_id: "", nip: "", nama: "", peran: "anggota", keterangan: "" });
  const [formRegu, setFormRegu] = useState({ nama_regu: "", keterangan: "" });
  const [formTukar, setFormTukar] = useState({ jadwal_id: "", nip_pengganti: "", alasan: "" });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rJadwal, rRegu, rTukar] = await Promise.all([
        PiketService.getJadwal(filterTgl ? { tanggal: filterTgl } : {}),
        PiketService.getRegu(),
        PiketService.getTukar(),
      ]);
      setJadwal(rJadwal?.data?.data || []);
      setRegu(rRegu?.data?.data || []);
      setTukar(rTukar?.data?.data || []);
      try {
        const rPeg = await scheduleService.getPegawai();
        setPegawai(rPeg?.data?.data || rPeg?.data || []);
      } catch (e) { /* pegawai opsional */ }
    } catch (e) {
      toast({ title: "Gagal memuat data piket", description: e?.response?.data?.message || e.message, status: "error" });
    } finally {
      setLoading(false);
    }
  }, [filterTgl, toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const submitJadwal = async () => {
    try {
      await PiketService.createJadwal({ ...formJadwal, regu_id: Number(formJadwal.regu_id) });
      toast({ title: "Jadwal piket disimpan", status: "success" });
      jadwalModal.onClose();
      setFormJadwal({ tanggal: todayStr(), regu_id: "", nip: "", nama: "", peran: "anggota", keterangan: "" });
      loadAll();
    } catch (e) {
      toast({ title: "Gagal simpan jadwal", description: e?.response?.data?.message || e.message, status: "error" });
    }
  };

  const submitRegu = async () => {
    try {
      await PiketService.createRegu(formRegu);
      toast({ title: "Regu disimpan", status: "success" });
      reguModal.onClose();
      setFormRegu({ nama_regu: "", keterangan: "" });
      loadAll();
    } catch (e) {
      toast({ title: "Gagal simpan regu", description: e?.response?.data?.message || e.message, status: "error" });
    }
  };

  const submitTukar = async () => {
    try {
      await PiketService.ajukanTukar({ ...formTukar, jadwal_id: Number(formTukar.jadwal_id) });
      toast({ title: "Pengajuan tukar dikirim", status: "success" });
      tukarModal.onClose();
      setFormTukar({ jadwal_id: "", nip_pengganti: "", alasan: "" });
      loadAll();
    } catch (e) {
      toast({ title: "Gagal ajukan tukar", description: e?.response?.data?.message || e.message, status: "error" });
    }
  };

  const prosesTukar = async (id, status) => {
    try {
      await PiketService.approveTukar(id, status);
      toast({ title: `Pengajuan ${status}`, status: status === "disetujui" ? "success" : "info" });
      loadAll();
    } catch (e) {
      toast({ title: "Gagal proses", description: e?.response?.data?.message || e.message, status: "error" });
    }
  };

  const statusColor = (s) => (s === "disetujui" ? "green" : s === "ditolak" ? "red" : "yellow");

  return (
    <Box pt="80px" px={4}>
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={2}>
        <Heading size="md">Piket Harian</Heading>
        <HStack>
          <Input type="date" value={filterTgl} onChange={(e) => setFilterTgl(e.target.value)} maxW="200px" bg="white" />
          <Button size="sm" onClick={() => setFilterTgl(todayStr())}>Hari ini</Button>
          <Button size="sm" leftIcon={<AddIcon />} colorScheme="blue" onClick={jadwalModal.onOpen}>Input Jadwal</Button>
          <Button size="sm" colorScheme="teal" onClick={reguModal.onOpen}>Atur Regu</Button>
          <Button size="sm" colorScheme="orange" onClick={tukarModal.onOpen}>Tukar Piket</Button>
        </HStack>
      </HStack>

      <Tabs variant="enclosed" bg="white" borderRadius="md">
        <TabList>
          <Tab>Jadwal ({jadwal.length})</Tab>
          <Tab>Regu ({regu.length})</Tab>
          <Tab>Tukar ({tukar.length})</Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0}>
            <Card p={4}>
              {loading ? (<Center py={10}><Spinner /></Center>) : jadwal.length === 0 ? (
                <Center py={10}><Text color="gray.500">Tidak ada jadwal piket pada tanggal ini.</Text></Center>
              ) : (
                <TableContainer>
                  <Table size="sm">
                    <Thead><Tr><Th>Tanggal</Th><Th>Regu</Th><Th>NIP</Th><Th>Nama</Th><Th>Peran</Th><Th>Keterangan</Th></Tr></Thead>
                    <Tbody>
                      {jadwal.map((j) => (
                        <Tr key={j.id}>
                          <Td>{j.tanggal}</Td>
                          <Td><Badge colorScheme="blue">{j.regu?.nama_regu || j.regu_id}</Badge></Td>
                          <Td>{j.nip}</Td>
                          <Td>{j.nama}</Td>
                          <Td>{j.peran}</Td>
                          <Td>{j.keterangan}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </TabPanel>
          <TabPanel p={0}>
            <Card p={4}>
              <TableContainer>
                <Table size="sm">
                  <Thead><Tr><Th>ID</Th><Th>Nama Regu</Th><Th>Keterangan</Th><Th>Status</Th></Tr></Thead>
                  <Tbody>
                    {regu.map((r) => (
                      <Tr key={r.id}>
                        <Td>{r.id}</Td><Td>{r.nama_regu}</Td><Td>{r.keterangan}</Td>
                        <Td><Badge colorScheme={r.aktif ? "green" : "gray"}>{r.aktif ? "Aktif" : "Nonaktif"}</Badge></Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Card>
          </TabPanel>
          <TabPanel p={0}>
            <Card p={4}>
              <TableContainer>
                <Table size="sm">
                  <Thead><Tr><Th>ID</Th><Th>Jadwal</Th><Th>Pemohon</Th><Th>Pengganti</Th><Th>Alasan</Th><Th>Status</Th><Th>Aksi</Th></Tr></Thead>
                  <Tbody>
                    {tukar.map((t) => (
                      <Tr key={t.id}>
                        <Td>{t.id}</Td><Td>{t.jadwal_id}</Td><Td>{t.nip_pemohon}</Td>
                        <Td>{t.nip_pengganti}</Td><Td>{t.alasan}</Td>
                        <Td><Badge colorScheme={statusColor(t.status)}>{t.status}</Badge></Td>
                        <Td>
                          {t.status === "menunggu" && (
                            <HStack>
                              <Button size="xs" colorScheme="green" onClick={() => prosesTukar(t.id, "disetujui")}>Setuju</Button>
                              <Button size="xs" colorScheme="red" onClick={() => prosesTukar(t.id, "ditolak")}>Tolak</Button>
                            </HStack>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Modal input jadwal */}
      <Modal isOpen={jadwalModal.isOpen} onClose={jadwalModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Input Jadwal Piket</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              <FormControl><FormLabel>Tanggal</FormLabel>
                <Input type="date" value={formJadwal.tanggal} onChange={(e) => setFormJadwal({ ...formJadwal, tanggal: e.target.value })} />
              </FormControl>
              <FormControl><FormLabel>Regu</FormLabel>
                <Select placeholder="Pilih regu" value={formJadwal.regu_id} onChange={(e) => setFormJadwal({ ...formJadwal, regu_id: e.target.value })}>
                  {regu.map((r) => (<option key={r.id} value={r.id}>{r.nama_regu}</option>))}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Pegawai</FormLabel>
                <Select placeholder="Pilih pegawai" value={formJadwal.nip} onChange={(e) => {
                  const p = pegawai.find((x) => String(x.NIP) === e.target.value);
                  setFormJadwal({ ...formJadwal, nip: e.target.value, nama: p?.NAMA || "" });
                }}>
                  {pegawai.map((p) => (<option key={p.NIP} value={p.NIP}>{p.NAMA} — {p.NIP}</option>))}
                </Select>
              </FormControl>
              <FormControl><FormLabel>Nama (manual jika pegawai tidak ada di list)</FormLabel>
                <Input value={formJadwal.nama} onChange={(e) => setFormJadwal({ ...formJadwal, nama: e.target.value })} />
              </FormControl>
              <FormControl><FormLabel>Peran</FormLabel>
                <Select value={formJadwal.peran} onChange={(e) => setFormJadwal({ ...formJadwal, peran: e.target.value })}>
                  <option value="anggota">Anggota</option>
                  <option value="komandan">Komandan Regu</option>
                  <option value="perwira">Perwira Piket</option>
                </Select>
              </FormControl>
              <FormControl><FormLabel>Keterangan</FormLabel>
                <Input value={formJadwal.keterangan} onChange={(e) => setFormJadwal({ ...formJadwal, keterangan: e.target.value })} />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={jadwalModal.onClose}>Batal</Button>
            <Button colorScheme="blue" onClick={submitJadwal}>Simpan</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal regu */}
      <Modal isOpen={reguModal.isOpen} onClose={reguModal.onClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Tambah Regu</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              <FormControl><FormLabel>Nama Regu</FormLabel>
                <Input placeholder="cth: Regu A" value={formRegu.nama_regu} onChange={(e) => setFormRegu({ ...formRegu, nama_regu: e.target.value })} />
              </FormControl>
              <FormControl><FormLabel>Keterangan</FormLabel>
                <Input value={formRegu.keterangan} onChange={(e) => setFormRegu({ ...formRegu, keterangan: e.target.value })} />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={reguModal.onClose}>Batal</Button>
            <Button colorScheme="teal" onClick={submitRegu}>Simpan</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal tukar */}
      <Modal isOpen={tukarModal.isOpen} onClose={tukarModal.onClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Ajukan Tukar Piket</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              <FormControl><FormLabel>Jadwal</FormLabel>
                <Select placeholder="Pilih jadwal" value={formTukar.jadwal_id} onChange={(e) => setFormTukar({ ...formTukar, jadwal_id: e.target.value })}>
                  {jadwal.map((j) => (<option key={j.id} value={j.id}>{j.tanggal} — {j.nama || j.nip}</option>))}
                </Select>
              </FormControl>
              <FormControl><FormLabel>NIP Pengganti</FormLabel>
                <Input value={formTukar.nip_pengganti} onChange={(e) => setFormTukar({ ...formTukar, nip_pengganti: e.target.value })} />
              </FormControl>
              <FormControl><FormLabel>Alasan</FormLabel>
                <Input value={formTukar.alasan} onChange={(e) => setFormTukar({ ...formTukar, alasan: e.target.value })} />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={tukarModal.onClose}>Batal</Button>
            <Button colorScheme="orange" onClick={submitTukar}>Ajukan</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
